import React, { useCallback, useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useBuildProjectLink } from "@/hooks/useProjectWithUsers";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { Canvas, Project } from "@/utils/types";
import {
  fixJson,
  getAxiosErorrMessage,
  getUnexpiredToken,
} from "@/utils/utils";
import axios from "axios";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { ChevronRightIcon } from "lucide-react";
import {
  CanvasAssignDropdown,
  CanvasLabelDropdown,
} from "./ProjectList/CanvasDropdown";
import Icon, { ICONS } from "@/components/Core/Icons";
import { useSupabase } from "./contexts/DatabaseSyncWrapper";
import { useNavigate } from "react-router";
import { cn } from "@/lib/utils";
import SyncedTipTapEditor from "./AI/PRD/SyncedTipTapEditor";
import { generateJsonStream } from "@/utils/ai/utils";
import { useSession } from "@clerk/clerk-react";

interface Props {
  openCanvasState: string;
  setOpenCanvasState: (open: boolean) => void;
  project: Project | null;
  createIndex: number;
}

const CreateCanvasModal: React.FC<Props> = ({
  openCanvasState,
  setOpenCanvasState,
  project,
  createIndex,
}) => {
  if (!project) return null;
  const initialFormState = {
    creating: false,
    featureName: "",
    featureDescription: "",
    canvasDetails: { labels: [], assigned_user_id: null as string | null },
  };
  const [formState, setFormState] = useState(initialFormState);
  const [isBlinking, setIsBlinking] = useState(false);

  const { triggerRefetchCanvasPreviews } = useSupabase();

  const { session } = useSession();

  const buildLink = useBuildProjectLink();

  const navigate = useNavigate();

  const { supabaseClient } = useSupabase();

  const editorRef = useRef<any>(null);

  const validateAndAction = (): boolean => {
    if (!formState.featureName) {
      toast.error("Title is required");
      return false;
    }
    const editorText = editorRef.current?.getText();
    if (!editorText) {
      toast.error("Description is required");
      return false;
    }
    return true;
  };

  const createCanvas = async () => {
    const canvasPayload = {
      assigned_user_id: formState.canvasDetails.assigned_user_id,
      labels: formState.canvasDetails.labels,
    };

    const supabaseJWT = await getUnexpiredToken(session);

    const res = await axios.post(
      `${import.meta.env.VITE_BACKEND_BASE_URL}/projects/${project.id}/canvas`,
      canvasPayload,
      { headers: { Authorization: `Bearer ${supabaseJWT}` } }
    );

    return res.data.canvas;
  };

  const savePRD = async (canvasId: any) => {
    if (!supabaseClient || !canvasId) {
      toast.error("Canvas ID is missing. Cannot save PRD.");
      return;
    }

    const newPrd = {
      id: uuidv4().toString(),
      canvas_id: canvasId,
      status: openCanvasState,
      order: createIndex,
      title: formState.featureName,
      prd: formState.featureDescription,
    };

    const sbStoreState = useSupabaseStore.getState();
    sbStoreState.onDbSnapshot("featurePrds", {
      newItems: [newPrd],
      deletedItems: [],
    });

    await supabaseClient.from("feature_prd").insert(newPrd);

    return newPrd;
  };

  const handleSuccess = (canvas: any) => {
    toast("New canvas created", {
      action: {
        label: "Open",
        onClick: () => {
          const link = buildLink(project, canvas);
          navigate(link);
        },
      },
    });

    // @ts-expect-error TS2345
    setOpenCanvasState(null);
    setFormState(initialFormState);
  };

  const onSubmit = async (): Promise<void> => {
    if (!validateAndAction()) return;

    setFormState((prevState) => ({ ...prevState, creating: true }));

    try {
      const createdCanvas = await createCanvas();
      setFormState((prevState) => ({
        ...prevState,
        canvasDetails: createdCanvas,
      }));

      await savePRD(createdCanvas?.id);

      triggerRefetchCanvasPreviews?.();

      handleSuccess(createdCanvas);
    } catch (err) {
      const message = getAxiosErorrMessage(err);
      toast.error(`Error creating canvas: ${message}`);
    } finally {
      setFormState((prevState) => ({ ...prevState, creating: false }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    setFormState((prevState) => ({
      ...prevState,
      featureName: value,
    }));
  };

  const validateAiEnhancePayload = () => {
    if (!formState.featureName && !editorRef.current?.getText()) {
      return false;
    }
    return true;
  };

  const aiEnhanceDescription = useCallback(async () => {
    if (!session) {
      return;
    }

    try {
      setIsBlinking(true);
      const url = `${
        import.meta.env.VITE_BACKEND_BASE_URL
      }/canvases/dummy-id/ai/vercel-ai/aiEnhanceDescription`;
      const body = {
        title: formState.featureName,
        description: formState.featureDescription,
      };

      const supabaseJWT = await getUnexpiredToken(session);

      const stream = await generateJsonStream(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${supabaseJWT}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      setFormState((prevState) => ({
        ...prevState,
        featureDescription: "",
      }));

      let partialResult = "";
      let res = null;

      for await (let chunk of stream) {
        if (chunk.status === -1 && chunk.message === "intermediate") {
          const parsedChunk = JSON.parse(chunk.data);

          if (parsedChunk.type === "text-delta") {
            let enhancedDescription = parsedChunk.chunk;
            partialResult += enhancedDescription;
          }
          setFormState((prevState) => ({
            ...prevState,
            featureDescription:
              fixJson(partialResult).fixedObj?.enhancedDescription || "",
          }));
        } else {
          res = chunk;
          break;
        }
      }

      // @ts-expect-error TS18047
      if (res.status >= 400) {
        console.error({
          // @ts-expect-error TS18047
          msg: res.message,
          // @ts-expect-error TS18047
          internalErrorStr: res.internalErrorStr,
        });
        // @ts-expect-error TS18047
        throw new Error(res.message);
      }
    } catch (err) {
      toast.error(`Error enhancing description: ${err.message}`);
    } finally {
      setIsBlinking(false);
    }
  }, [formState.featureName, formState.featureDescription, Boolean(session)]);

  return (
    <Dialog
      open={Boolean(openCanvasState)}
      onOpenChange={() => {
        setOpenCanvasState(false);
        setFormState(initialFormState);
      }}
      modal={false}
    >
      <DialogContent
        className="sm:max-w-[425px] md:min-w-[500px] bg-secondary-n-950 p-3 gap-2.5"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="text-xs leading-none gap-1.5 flex flex-row justify-start items-start py-2 w-full max-w-full truncate">
            {project?.name && (
              <div
                className="truncate shrink max-w-48 text-muted-foreground"
                title={project.name}
              >
                {project.name}
              </div>
            )}
            <ChevronRightIcon
              aria-hidden="true"
              className="w-3 h-3 text-icons-secondary"
            />
            <div
              className="cursor-pointer text-foreground hover:underline truncate shrink max-w-44"
              title={project?.repoPath || ""}
            >
              New Feature
            </div>
          </div>
        </DialogHeader>
        <div className="flex flex-col">
          <Input
            id="name"
            className={cn(
              "h-6 font-medium bg-secondary-n-950 border-none focus:border-transparent focus:bg-secondary-n-950 focus:outline-none focus:ring-0 flex-grow text-base leading-5 placeholder-secondary-n-600 p-1 focus-visible:ring-0 mb-1"
            )}
            placeholder="Feature Title"
            value={formState.featureName}
            onInput={handleInputChange}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                editorRef.current?.focus();
              }
            }}
          />

          <SyncedTipTapEditor
            ref={editorRef}
            classnames="!min-h-32 !max-h-64 px-1 py-3 !text-sm !leading-5 !placeholder-secondary-n-600"
            placeholder={"Add description..."}
            valueInSupabase={formState.featureDescription}
            onUpdateValueInSupabase={(newValue) => {
              setFormState((prevState) => ({
                ...prevState,
                featureDescription: newValue,
              }));
            }}
          />
        </div>
        <div className="flex sm:flex-row flex-col sm:justify-between sm:items-center">
          <div className="flex items-center gap-2 text-xs h-7 sm:px-2 sm:mb-0 mb-1">
            <CanvasAssignDropdown
              projectId={project.id}
              canvas={formState.canvasDetails as unknown as Canvas}
              onChange={(userId) => {
                setFormState((prevState) => ({
                  ...prevState,
                  canvasDetails: {
                    ...prevState.canvasDetails,
                    assigned_user_id: userId,
                  },
                }));
              }}
              type="new"
            />
            <CanvasLabelDropdown
              canvas={formState.canvasDetails as unknown as Canvas}
              onChange={(newLabels) =>
                // @ts-expect-error TS2345
                setFormState((prevState) => ({
                  ...prevState,
                  canvasDetails: {
                    ...prevState.canvasDetails,
                    labels: newLabels,
                  },
                }))
              }
              type="new"
            />
            <Button
              size="icon"
              data-tooltip-id="tooltip"
              data-tooltip-content={"AI"}
              className={cn(
                "!relative !h-6 !w-6 !text-xs !rounded-md !px-0  !hover:bg-border-secondary-n-900 cursor-pointer bg-background-tertiary !hover:bg-border-secondary-n-900 !hover:border-0",
                formState.creating ? "animate-pulse" : ""
              )}
              onClick={aiEnhanceDescription}
              variant="outline"
              disabled={!validateAiEnhancePayload()}
            >
              <Icon
                icon={ICONS.AI_LOGO}
                className={cn(
                  "!absolute transition-colors opacity-20 group-hover:opacity-100 w-4 h-4",
                  isBlinking ? "animate-fast-pulse" : "",
                  {
                    "opacity-20": !isBlinking,
                  }
                )}
              />
              <Icon
                icon={ICONS.STAR}
                width={6}
                height={6}
                className={cn(
                  "opacity-20 !absolute right-0.5 top-0.5",
                  isBlinking ? "animate-fast-pulse" : "",
                  {
                    "opacity-100": !isBlinking,
                  }
                )}
              />
            </Button>
          </div>
          <Button
            className="flex items-center gap-1 text-xs h-6 px-2 w-fit"
            type="submit"
            variant="primary"
            onClick={onSubmit}
            disabled={formState.creating}
          >
            {formState.creating ? "Creating..." : "Create Feature"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(CreateCanvasModal);
